import { Component, effect, inject, model, signal } from '@angular/core';
import { Title } from '@angular/platform-browser';
import { forkJoin, map, Subject, takeUntil } from 'rxjs';
import {
	ApiRequestOrdering,
	RentalAgreementSearchDto,
	VehicleMaintenanceSearchDto,
} from '@splitsecondconnect/ultramarine-shared';

import { ModulesEnum } from '@splitsecondconnect/ultramarine-shared';
import { SelectItem } from '../../../../shared/models/select-item.type';
import { mapDtoToModelRentalAgreements } from '../../rental-agreements/mappers/search-dto-to-model.mapper';
import { RentalAgreementsService } from '../../rental-agreements/rental-agreements.service';
import { mapDtoToModelVehicleMaintenances } from '../../vehicle-maintenances/mappers/search-dto-to-model.mapper';
import { VehicleMaintenancesService } from '../../vehicle-maintenances/vehicle-maintenances.service';
import { WebsocketService } from '../../../../shared/services/web-socket.service';

@Component({
	selector: 'app-timeline',
	templateUrl: 'timeline.component.html',
	host: {
		class: 'h-screen',
	},
})
// eslint-disable-next-line @typescript-eslint/no-unused-vars
export class TimelineComponent<TSearchDto, TListFilters> {
	defaultSortingRentalAgreement: ApiRequestOrdering = {
		columnName: `rental_agreement.id`,
		direction: 'DESC',
	};

	defaultSortingMaintenance: ApiRequestOrdering = {
		columnName: `vehicle_maintenance.id`,
		direction: 'DESC',
	};
	public data =
		signal<
			SelectItem<RentalAgreementSearchDto | VehicleMaintenanceSearchDto>[]
		>(null);
	filters = model<TListFilters>();
	modulesEnum = ModulesEnum;
	#rentals = inject(RentalAgreementsService);
	#maintenances = inject(VehicleMaintenancesService);
	search = signal<string>(null);
	#title = inject(Title);
	pageNumber = signal<number>(0);
	public totalCount: number;
	public pageSize: number;
	protected _unsubscribeAll: Subject<any> = new Subject();
	private _websocketService = inject(WebsocketService);

	pingerSignal = signal(1);

	ngOnDestroy() {
		this._unsubscribeAll.next(null);
		this._unsubscribeAll.complete();
	}

	ngOnInit() {
		this._websocketService
			.getMultipleRecordUpdate([
				ModulesEnum.RENTAL_AGREEMENTS,
				ModulesEnum.VEHICLE_MAINTENANCES,
			])
			.pipe(takeUntil(this._unsubscribeAll))
			.subscribe(() => {
				if (this.pingerSignal() === 1) {
					this.pingerSignal.set(0);
				} else {
					this.pingerSignal.set(1);
				}
			});

		this._websocketService
			.getMultipleRecordStatusUpdate([
				ModulesEnum.RENTAL_AGREEMENTS,
				ModulesEnum.VEHICLE_MAINTENANCES,
			])
			.pipe(takeUntil(this._unsubscribeAll))
			.subscribe(() => {
				if (this.pingerSignal() === 1) {
					this.pingerSignal.set(0);
				} else {
					this.pingerSignal.set(1);
				}
			});
	}

	constructor() {
		this.#title.setTitle('Timeline');
		effect(() => {
			const onPing = this.pingerSignal(); // to trigger the effect on pinger change.
			forkJoin({
				rentals: this.#rentals
					.getSearchResults(
						this.search(),
						null,
						10,
						this.filters(),
						this.pageNumber(),
						this.defaultSortingRentalAgreement,
					)
					.pipe(map((res) => mapDtoToModelRentalAgreements(res.data))),
				maintenances: this.#maintenances
					.getSearchResults(
						this.search(),
						null,
						10,
						this.filters(),
						this.pageNumber(),
						this.defaultSortingMaintenance,
					)
					.pipe(map((res) => mapDtoToModelVehicleMaintenances(res.data))),
			}).subscribe(({ rentals, maintenances }) => {
				const combinedData = [...rentals, ...maintenances];
				this.data.set(combinedData);
			});
		});
	}
}
